import {
  useState,
  useRef,
  useEffect,
  forwardRef,
} from "react";
import DocumentUpload from "./documentUpload/DocumentUpload";
import PatientDetail from "./patientDetail/PatientDetail";
import IntakeSheet from "./intakeSheet/IntakeSheet";
import "../styles/MultiPatientPortal.css";
import toast from "react-hot-toast";
import { GetPatientDetails } from "./patientDetail/Service";
import { CallApiForGetCaseDocuments } from "../helpers/http";
import { usePatientData } from "../commonFunction/storage/Patientdatacontext";
import { Dropdown } from "react-bootstrap";
import { setSessionStorage } from "../helpers/storage";
import CompletionScreen from "./completionScreen/CompletionScreen";


/* ── Custom Toggles for React Bootstrap Dropdown ── */
const PatientToggle = forwardRef(({ children, onClick }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="pp-user-pill"
    style={{ cursor: "pointer", display: "flex", alignItems: "center", border: "none", background: "none" }}
  >
    {children}
  </div>
));

const LocationToggle = forwardRef(({ children, onClick }, ref) => (
  <span
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="pp-tag pp-tag--teal"
    style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
  >
    {children}
  </span>
));

const MobileLocationToggle = forwardRef(({ children, onClick }, ref) => (
  <span
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="pp-location-bar__text"
    style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}
  >
    {children}
  </span>
));

const MobileIconToggle = forwardRef(({ children, onClick, title, showArrow }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    title={title}
    style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", background: "none", border: "none" }}
  >
    <div className="pp-mobile-icon-toggle">
      {children}
    </div>
    {showArrow && (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: "4px", color: "#2a9d8f" }}>
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    )}
  </div>
));

/* ── Design tokens ── */
const C = {
  bg: "#f4efe8",
  cardBg: "#ffffff",
  s50: "#faf7f2",
  s100: "#f0e9de",
  s200: "#e2d5c3",
  s300: "#c9b89e",
  t400: "#2a9d8f",
  t500: "#1f8a7e",
  t600: "#176b62",
  t700: "#0f4e47",
  coral: "#e07b54",
  coralBg: "#fdf1ec",
  dk: "#1a1410",
  mid: "#3d332a",
  muted: "#7d6e61",
  light: "#b0a090",
  border: "#e5ddd3",
  ibg: "#fdfbf8",
  white: "#ffffff",
  glow: "rgba(42,157,143,0.18)",
};

/* ── Steps config ── */
// Note: We now build ACTIVE_STEPS dynamically inside the component
const STEP_DESCS = {
  patient: "Verify your identity and contact information carefully.",
  intake: "Complete all intake sheets — accident, insurance, authorization and referral.",
  documents: "Upload and manage your documents securely.",
};

/* ── Logo ── */
function Logo() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      <rect width="32" height="32" rx="8" fill={C.t500} />
      <circle
        cx="16"
        cy="16"
        r="9"
        stroke="white"
        strokeWidth="1.4"
        fill="none"
      />
      <line x1="16" y1="7" x2="16" y2="25" stroke="white" strokeWidth="1.4" />
      <line x1="7" y1="16" x2="25" y2="16" stroke="white" strokeWidth="1.4" />
      <path
        d="M9 12 Q16 9.5 23 12"
        stroke="white"
        strokeWidth="1.4"
        fill="none"
      />
      <path
        d="M9 20 Q16 22.5 23 20"
        stroke="white"
        strokeWidth="1.4"
        fill="none"
      />
    </svg>
  );
}

function computePatientPending(details) {
  if (!details) return true;
  const first = (details.patientFirstName || "").trim();
  const last = (details.patientLastName || "").trim();
  return !first || !last;
}

function computeDocsPending(uploadedMap, tokenDocuments) {
  if (!tokenDocuments?.length) return false;
  return tokenDocuments.some(
    (doc) => !uploadedMap[String(doc.DocumentId ?? doc.documentId)],
  );
}

function computeIntakePendingInitial(intakeSheetData) {
  if (!intakeSheetData || !Array.isArray(intakeSheetData)) return true;
  const allSaved = intakeSheetData.every(
    (s) => s.IsSaved === true || s.isSaved === true,
  );
  return !allSaved;
}

function ScrollableStepTabs({
  steps,
  step,
  setStep,
  stepPendingMap,
  getStepSubLabel,
  getStepSubColor,
}) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  const setRef = (el) => {
    scrollRef.current = el;
    if (el) {
      el.addEventListener("scroll", checkScroll, { passive: true });
      setTimeout(checkScroll, 0);
    }
  };

  useEffect(() => {
    const onResize = () => checkScroll();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const scroll = (dir) =>
    scrollRef.current?.scrollBy({ left: dir * 180, behavior: "smooth" });

  const ArrowBtn = ({ dir }) => {
    const visible = dir === -1 ? canLeft : canRight;
    return (
      <button
        onClick={() => scroll(dir)}
        className={`pp-nav-arrow pp-nav-arrow--${dir === -1 ? "left" : "right"}${!visible ? " pp-nav-arrow--hidden" : ""}`}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--pp-t600)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {dir === -1 ? (
            <polyline points="15 18 9 12 15 6" />
          ) : (
            <polyline points="9 18 15 12 9 6" />
          )}
        </svg>
      </button>
    );
  };

  return (
    <div className="pp-nav-tabs-wrapper">
      <ArrowBtn dir={-1} />
      <nav ref={setRef} className="pp-nav-tabs" onScroll={checkScroll}>
        {steps.map((s, i) => {
          const done = i < step;
          const active = i === step;
          const hasPending = stepPendingMap[s.originalIndex ?? i] ?? false;
          const subLabel = getStepSubLabel(i, done, active);
          const subColor = getStepSubColor(i, done, active);
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center" }}>
              <button
                onClick={() => setStep(i)}
                className={`pp-stab${active ? " pp-stab--active" : ""}`}
                style={{ color: active ? C.t600 : done ? C.t500 : C.muted }}
              >
                <div
                  className={`pp-stab__num${done && !hasPending
                    ? " pp-stab__num--done"
                    : active || (done && hasPending)
                      ? " pp-stab__num--active"
                      : hasPending
                        ? " pp-stab__num--active"
                        : " pp-stab__num--inactive"
                    }`}
                >
                  {done && !hasPending ? (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                      <polyline
                        points="2,6 5,9 10,3"
                        stroke="white"
                        strokeWidth="2.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    s.num
                  )}
                </div>
                <div>
                  <div
                    className="pp-stab__label"
                    style={{ fontWeight: active ? 700 : 500 }}
                  >
                    {s.label}
                  </div>
                  <div className="pp-stab__sub" style={{ color: subColor }}>
                    {subLabel}
                  </div>
                </div>
              </button>
              {i < steps.length - 1 && (
                <svg
                  className="pp-stab__arrow"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M3 2l4 3-4 3"
                    stroke={C.s300}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          );
        })}
      </nav>
      <ArrowBtn dir={1} />
    </div>
  );
}

export default function MultiPatientPortal() {
  const [refreshKey, setRefreshKey] = useState(0);

  const { patientData, fetchPatientData, refreshPatientData } = usePatientData();

  const patientDetailRef = useRef(null);
  const intakeSheetRef = useRef(null);
  const docUploadRef = useRef(null);
  const medicalNotesRef = useRef(null);

  const [mountedSteps, setMountedSteps] = useState({ 0: true, 1: true });

  const patientDetailsRaw = JSON.parse(
    localStorage.getItem("patientDetails") || "{}",
  );
  const localPatientDetails = patientDetailsRaw?.data || patientDetailsRaw;
  const patientDetails = patientData || localPatientDetails;

  // const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const [tokenData, setTokenData] = useState(() =>
  JSON.parse(sessionStorage.getItem("tokenData") || "{}")
);

const tokenDocuments = Array.isArray(tokenData?.documents)
  ? tokenData.documents
  : [];

const tokenIntakeSheets = Array.isArray(tokenData?.intakeSheets)
  ? tokenData.intakeSheets
  : [];

const caseId = tokenData?.caseId || tokenData?.CaseId || "";
const companyId = tokenData?.companyId || tokenData?.CompanyId || "";

  const doctorName =
    tokenData?.DoctorName || tokenData?.doctorName || patientDetails?.DoctorName || patientDetails?.doctorName || "";
  const specialtyName =
    tokenData?.DoctorSpecialtyName || tokenData?.doctorSpecialtyName || patientDetails?.DoctorSpecialtyName || patientDetails?.doctorSpecialtyName || "";
  const providerRaw =
    tokenData?.ProviderName || tokenData?.providerName || patientDetails?.ProviderName || patientDetails?.providerName || "";
  const providerName = providerRaw.includes("-")
    ? providerRaw.split("-")[0].trim()
    : providerRaw;
  const locationLabel = [doctorName, specialtyName, providerName]
    .filter(Boolean)
    .join("-");

  const [step, setStep] = useState(0);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [copySignature, setCopySignature] = useState(false);

  const [patientHasPending, setPatientHasPending] = useState(true);
  const [intakeHasPending, setIntakeHasPending] = useState(true);
  const [docsHasPending, setDocsHasPending] = useState(true);
  const [intakeTabInfo, setIntakeTabInfo] = useState(() => ({
    isLastTab: tokenIntakeSheets.length <= 1,
    isOnlyTab: tokenIntakeSheets.length <= 1,
    totalTabs: tokenIntakeSheets.length,
    currentIndex: 0,
  }));
  const ptFirst = patientDetails?.patientFirstName || patientDetails?.FirstName || tokenData?.firstName || tokenData?.FirstName || "";
  const ptLast = patientDetails?.patientLastName || patientDetails?.LastName || tokenData?.lastName || tokenData?.LastName || "";
  const patientName = `${ptFirst} ${ptLast}`.trim() || 'User';
  const patientFirstName = ptFirst || 'User';
  const patientNameInitials = `${ptFirst.charAt(0) || ""}${ptLast.charAt(0) || ""}`.toUpperCase() || "?";

  // ── Build Dynamic Steps ──
  const hasIntake = Array.isArray(tokenIntakeSheets) && tokenIntakeSheets.length > 0;
  const hasDocuments = Array.isArray(tokenDocuments) && tokenDocuments.length > 0;

  const ACTIVE_STEPS = [
    { id: "patient", label: "Patient Details", originalIndex: 0, num: "01" },
  ];

  if (tokenIntakeSheets.length > 0) {
    ACTIVE_STEPS.push({
      id: "intake",
      label: "Intake Sheets",
      originalIndex: 1,
      num: String(ACTIVE_STEPS.length + 1).padStart(2, "0"),
    });
  }

  if (tokenDocuments.length > 0) {
    ACTIVE_STEPS.push({
      id: "documents",
      label: "Documents",
      originalIndex: 2,
      num: String(ACTIVE_STEPS.length + 1).padStart(2, "0"),
    });
  }

  const currentStepObj = ACTIVE_STEPS[step] || ACTIVE_STEPS[0];
  const currentStepId = currentStepObj.id;
  const currentOriginalIndex = currentStepObj.originalIndex;



  // ── Multiple Patients & Locations Logic ──
  const allPatientsList = JSON.parse(sessionStorage.getItem("allPatientsList") || "[]");
  const authTokens = JSON.parse(sessionStorage.getItem("authTokens") || "{}");

  /* Save all unique providerIds for a patient as a comma-separated string */
  const saveUniqueProviderIds = (patientId) => {
    const records = allPatientsList.filter(
      p => (p.patientId || p.PatientId) === patientId
    );
    const ids = [
      ...new Set(
        records
          .map(p => p.providerId || p.ProviderId || p.provider_id || "")
          .filter(Boolean)
          .map(String)
      )
    ];
    setSessionStorage("providerIds", ids.join(","));
  };

  const uniquePatientsMap = new Map();
  allPatientsList.forEach(p => {
    const id = p.patientId || p.PatientId;
    if (id && !uniquePatientsMap.has(id)) {
      uniquePatientsMap.set(id, p);
    } else if (!id) {
      uniquePatientsMap.set(Math.random(), p);
    }
  });
  const uniquePatients = Array.from(uniquePatientsMap.values());

  const currentPatientId = tokenData?.patientId || tokenData?.PatientId;
  const rawAvailableLocations = currentPatientId
    ? allPatientsList.filter(p => (p.patientId || p.PatientId) === currentPatientId)
    : [];

  const uniqueLocMap = new Map();
  rawAvailableLocations.forEach((loc, idx) => {
    const docName = loc.DoctorName || loc.doctorName || "";
    const spec = loc.DoctorSpecialtyName || loc.doctorSpecialtyName || "";
    const prov = loc.ProviderName || loc.providerName || "";
    const pName = prov.includes("-") ? prov.split("-")[0].trim() : prov;
    const label = [docName, spec, pName].filter(Boolean).join("-") || `Location ${idx + 1}`;

    if (!uniqueLocMap.has(label)) {
      uniqueLocMap.set(label, loc);
    }
  });
  const availableLocations = Array.from(uniqueLocMap.values());

const handlePatientSwitch = (pId) => {
  const newRecord = allPatientsList.find(
    (p) => (p.patientId || p.PatientId) === pId
  );

  if (!newRecord) return;

  const updatedTokenData = {
    ...newRecord,
    ...authTokens,
    intakeSheets:
      newRecord.intakeSheets ??
      newRecord.IntakeSheets ??
      [],
    documents:
      newRecord.documents ??
      newRecord.Documents ??
      [],
  };

  sessionStorage.setItem(
    "tokenData",
    JSON.stringify(updatedTokenData)
  );

  // Keep existing consumers of these session-storage keys synchronized.
  sessionStorage.setItem(
    "tokenIntakeSheets",
    JSON.stringify(updatedTokenData.intakeSheets)
  );

  sessionStorage.setItem(
    "tokenDocuments",
    JSON.stringify(updatedTokenData.documents)
  );

  setTokenData(updatedTokenData);

  localStorage.removeItem("patientDetails");
  saveUniqueProviderIds(pId);

  if (refreshPatientData) {
    refreshPatientData(
      updatedTokenData.caseId || updatedTokenData.CaseId,
      updatedTokenData.companyId || updatedTokenData.CompanyId
    );
  }

  setUploadedDocs({});
  setIntakeHasPending(updatedTokenData.intakeSheets.length > 0);
  setDocsHasPending(updatedTokenData.documents.length > 0);
  setMountedSteps({ 0: true, 1: true });
  setRefreshKey((prev) => prev + 1);
  setStep(0);
};

const handleLocationSwitch = (locationKey) => {
  const newRecord = allPatientsList.find((p) => {
    const key =
      `${p.doctorId || p.DoctorId}-` +
      `${p.doctorSpecialtyId || p.DoctorSpecialtyId}-` +
      `${p.providerId || p.ProviderId}`;

    return (
      key === locationKey &&
      (p.patientId || p.PatientId) === currentPatientId
    );
  });

  if (!newRecord) return;

  const updatedTokenData = {
    ...newRecord,
    ...authTokens,
    intakeSheets: tokenData.intakeSheets,
    documents: tokenData.documents,
  };

  sessionStorage.setItem(
    "tokenData",
    JSON.stringify(updatedTokenData)
  );

  setTokenData(updatedTokenData);
  localStorage.removeItem("patientDetails");

  if (refreshPatientData) {
    refreshPatientData(
      updatedTokenData.caseId || updatedTokenData.CaseId,
      updatedTokenData.companyId || updatedTokenData.CompanyId
    );
  }

  setRefreshKey((prev) => prev + 1);
  setStep(0);
};

  /* ── true when intake sheet or documents step is active (fill full screen) ── */
  const isFullHeightStep = currentStepId === "intake" || currentStepId === "documents";

  const [showPatientHint, setShowPatientHint] = useState(false);
  const [showLocationHint, setShowLocationHint] = useState(false);

  useEffect(() => {
    const hasMultiplePatients = uniquePatients.length > 1;
    const hasMultipleLocations = availableLocations.length > 1;

    if (hasMultiplePatients) setShowPatientHint(true);
    if (hasMultipleLocations) setShowLocationHint(true);

    // Save unique providerIds for the currently selected patient on first load
    if (currentPatientId) saveUniqueProviderIds(currentPatientId);

    const timer = setTimeout(() => {
      setShowPatientHint(false);
      setShowLocationHint(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []); // Run only on initial mount



  useEffect(() => {
    if (!caseId || !companyId) return;

    const checkAllSteps = async () => {
      await Promise.allSettled([
        (async () => {
          try {
            const res = await GetPatientDetails(caseId, companyId);
            if (res?.success && res?.data) {
              setPatientHasPending(computePatientPending(res.data));
            }
          } catch {
            /* leave as pending */
          }
        })(),

        (async () => {
          try {
            const raw = sessionStorage.getItem("tokenIntakeSheets");
            if (!raw) {
              setIntakeHasPending(true);
              return;
            }
            const sheets = JSON.parse(raw);
            const pending = computeIntakePendingInitial(sheets);
            setIntakeHasPending(pending);
          } catch {
            setIntakeHasPending(true);
          }
        })(),

        (async () => {
          try {
            const res = await CallApiForGetCaseDocuments(caseId);
            if (!res?.success || !Array.isArray(res.data)) {
              setDocsHasPending(tokenDocuments.length > 0);
              return;
            }
            const uploadedMap = {};
            res.data.forEach((doc) => {
              const key = String(doc.documentType ?? doc.documentId ?? doc.nodeId);
              uploadedMap[key] = true;
            });
            setDocsHasPending(computeDocsPending(uploadedMap, tokenDocuments));
          } catch {
            setDocsHasPending(tokenDocuments.length > 0);
          }
        })(),
      ]);
    };

    checkAllSteps();
  }, [caseId, companyId]);

  useEffect(() => {
    setMountedSteps((prev) => ({ ...prev, [currentOriginalIndex]: true }));
  }, [currentOriginalIndex]);

  const stepPendingMap = {
    0: patientHasPending,
    1: intakeHasPending,
    2: docsHasPending,
  };

  const tabSwitchTargetRef = useRef(null);
  const isFinishActionRef = useRef(false);
  const [allDone, setAllDone] = useState(false);

  const completedCount = ACTIVE_STEPS.filter((s, i) => {
    if (stepPendingMap[s.originalIndex] !== undefined) return !stepPendingMap[s.originalIndex];
    return step > i;
  }).length;

  const pct = Math.round((completedCount / ACTIVE_STEPS.length) * 100);

  const handleStepSuccess = () => {
    // TAB-SWITCH: if a target step is queued, jump there instead of advancing.
    if (tabSwitchTargetRef.current !== null) {
      const target = tabSwitchTargetRef.current;
      tabSwitchTargetRef.current = null;
      setStep(target);
      return;
    }
    if (step < ACTIVE_STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      // Only show CompletionScreen when the Finish button was explicitly clicked and >= 2 steps exist.
      if (isFinishActionRef.current && ACTIVE_STEPS.length >= 2) {
        setAllDone(true);
      }
      isFinishActionRef.current = false; // always reset
    }
  };

  const handleGoToDashboard = () => {
    setAllDone(false);
    setStep(0);
  };

  const handleGoToStep = (stepIndex) => {
    setAllDone(false);
    setStep(stepIndex);
  };

  // ── TAB-SWITCH: clicking a tab runs the same validate+save as Save & Continue
  const handleStepChange = (targetStep) => {
    if (targetStep === step) return;
    tabSwitchTargetRef.current = targetStep;

    if (currentStepId === "patient") patientDetailRef.current?.submit();
    else if (currentStepId === "intake") intakeSheetRef.current?.submit({ skipTabAdvance: true });
    else if (currentStepId === "documents") docUploadRef.current?.submit();
    else tabSwitchTargetRef.current = null;
  };

  const handlePatientReady = () => {
    const pending = patientDetailRef.current?.isPending?.() ?? false;
    setPatientHasPending(pending);
  };

  const handleSaveAndContinue = () => {
    const isLastPortalStep = step === ACTIVE_STEPS.length - 1;
    const isIntakeLastOrOnlyTab = intakeTabInfo.isLastTab || intakeTabInfo.isOnlyTab;

    if (isLastPortalStep && ACTIVE_STEPS.length >= 2) {
      if (currentStepId === "intake") {
        isFinishActionRef.current = isIntakeLastOrOnlyTab;
      } else {
        isFinishActionRef.current = true;
      }
    } else {
      isFinishActionRef.current = false;
    }

    if (currentStepId === "patient") patientDetailRef.current?.submit();
    else if (currentStepId === "intake") intakeSheetRef.current?.submit();
    else if (currentStepId === "documents") {
      docUploadRef.current?.submit();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userData");
    localStorage.removeItem("patientDetails");
    sessionStorage.clear();
    const tokenUrl = localStorage.getItem("tokenUrl");
    window.location.href = tokenUrl || "/login";
  };

  const getStepSubLabel = (i, done, active) => {
    if (active) return "In progress";
    const originalIdx = ACTIVE_STEPS[i]?.originalIndex;
    if (originalIdx !== undefined && stepPendingMap[originalIdx] !== undefined)
      return stepPendingMap[originalIdx] ? "Pending" : "Completed";
    return done ? "Completed" : "Pending";
  };

  const getStepSubColor = (i, done, active) => {
    if (active) return C.t500;
    const originalIdx = ACTIVE_STEPS[i]?.originalIndex;
    if (originalIdx !== undefined && stepPendingMap[originalIdx] !== undefined)
      return stepPendingMap[originalIdx] ? "#c2610c" : C.t400;
    return done ? C.t400 : "#c2610c";
  };



  return (
    <>
      {allDone && ACTIVE_STEPS.length >= 2 && (
        <CompletionScreen
          firstName={patientFirstName}
          onGoToDashboard={handleGoToDashboard}
          onLogout={handleLogout}
          onGoToStep={handleGoToStep}
          onClose={() => setAllDone(false)}
          availableSteps={ACTIVE_STEPS}
          stepStatus={{
            patientHasPending,
            intakeHasPending,
            docsHasPending,
          }}
        />
      )}
      <div
        style={{
          height: "100vh",
          background: C.bg,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ══ Header ══ */}
        <header className="pp-header">
          {/* ── Top bar ── */}
          <div className="pp-topbar">
            <div className="pp-topbar__left">
              <Logo />
              <div className="pp-brand">
                <span className="pp-brand__name">Patient Portal</span>
                <span className="pp-brand__sub">BY GREEN BILLS</span>
              </div>

              {/* ── Mobile Icons (≤540px) ── */}
              <div className="pp-mobile-icons">
                {/* Mobile Patient Dropdown / Icon */}
                <div className="pp-mobile-icon-wrapper">
                  {uniquePatients.length > 1 ? (
                    <>
                      <Dropdown className="d-inline-block" align="end" onToggle={(isOpen) => { if (isOpen) { setShowPatientHint(false); setShowLocationHint(false); } }}>
                        <Dropdown.Toggle as={MobileIconToggle} id="dropdown-patient-mobile" title={patientName} showArrow={true}>
                          {(ptFirst?.charAt(0) || "") + (ptLast?.charAt(0) || "")}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="pp-premium-dropdown" popperConfig={{ strategy: "fixed", modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}>
                          {uniquePatients.map((p, idx) => {
                            const pId = p.patientId || p.PatientId;
                            let name = `${p.firstName || p.FirstName} ${p.lastName || p.LastName}`;
                            if (pId === currentPatientId) {
                              name = patientName;
                            }
                            return (
                              <Dropdown.Item
                                key={idx}
                                className="pp-premium-dropdown-item"
                                active={pId === currentPatientId}
                                onClick={() => handlePatientSwitch(pId)}
                              >
                                <span style={{ flex: 1 }}>{name}</span>
                                {pId === currentPatientId && (
                                  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" style={{ marginLeft: "8px", flexShrink: 0 }}>
                                    <path d="M1 5l4 4L12 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </Dropdown.Item>
                            );
                          })}
                        </Dropdown.Menu>
                      </Dropdown>
                      {showPatientHint && (
                        <div className="pp-dropdown-hint pp-dropdown-hint--mobile-left" onClick={() => setShowPatientHint(false)}>
                          Change patient
                        </div>
                      )}
                    </>
                  ) : (
                    <button type="button" className="pp-mobile-icon-toggle" title={patientName}>
                      {(ptFirst?.charAt(0) || "") + (ptLast?.charAt(0) || "")}
                    </button>
                  )}
                </div>

                {/* Mobile Location Dropdown / Icon */}
                <div className="pp-mobile-icon-wrapper">
                  {availableLocations.length > 1 ? (
                    <>
                      <Dropdown className="d-inline-block" align="end" onToggle={(isOpen) => { if (isOpen) { setShowPatientHint(false); setShowLocationHint(false); } }}>
                        <Dropdown.Toggle as={MobileIconToggle} id="dropdown-location-mobile" title={locationLabel || "No location assigned"} showArrow={true}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="pp-premium-dropdown" popperConfig={{ strategy: "fixed", modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}>
                          {availableLocations.map((loc, idx) => {
                            const eId = `${loc.doctorId}-${loc.doctorSpecialtyId}-${loc.providerId}`;
                            const docName = loc.doctorName || "";
                            const spec = loc.doctorSpecialtyName || "";
                            const prov = loc.providerName || "";
                            const pName = prov.includes("-") ? prov.split("-")[0].trim() : prov;
                            const label = [docName, spec, pName].filter(Boolean).join("-") || `Location ${idx + 1}`;

                            return (
                              <Dropdown.Item
                                key={eId || idx}
                                className="pp-premium-dropdown-item"
                                active={eId ===`${tokenData?.doctorId}-${tokenData?.doctorSpecialtyId}-${tokenData?.providerId}`}
                                onClick={() => handleLocationSwitch(eId)}
                              >
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                               {eId ===
                                      `${tokenData?.doctorId || tokenData?.DoctorId}-` +
                                      `${tokenData?.doctorSpecialtyId || tokenData?.DoctorSpecialtyId}-` +
                                      `${tokenData?.providerId || tokenData?.ProviderId}` && (
                                  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" style={{ marginLeft: "8px", flexShrink: 0 }}>
                                    <path d="M1 5l4 4L12 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </Dropdown.Item>
                            );
                          })}
                        </Dropdown.Menu>
                      </Dropdown>
                      {showLocationHint && (
                        <div className="pp-dropdown-hint pp-dropdown-hint--mobile-right" onClick={() => setShowLocationHint(false)}>
                          Change location
                        </div>
                      )}
                    </>
                  ) : (
                    <button type="button" className="pp-mobile-icon-toggle" title={locationLabel || "No location assigned"}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="pp-desktop-patient-wrap">
                {uniquePatients.length > 1 ? (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <Dropdown
                      className="d-inline-block"
                      onToggle={(isOpen) => {
                        if (isOpen) {
                          setShowPatientHint(false);
                          setShowLocationHint(false);
                        }
                      }}
                    >
                      <Dropdown.Toggle as={PatientToggle} id="dropdown-patient">
                        <div className="pp-user-pill__avatar">{patientNameInitials}</div>
                        <div className="pp-user-pill__name" style={{ textAlign: "left", marginRight: "8px" }}>
                          <div className="pp-user-pill__welcome">Welcome</div>
                          <div className="pp-user-pill__fullname">{patientName}</div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="pp-premium-dropdown">
                        {uniquePatients.map((p, idx) => {
                          const pId = p.patientId || p.PatientId;
                          let name = `${p.firstName || p.FirstName} ${p.lastName || p.LastName}`;
                          if (pId === currentPatientId) {
                            name = patientName; // Use freshly updated name for active patient
                          }
                          return (
                            <Dropdown.Item
                              key={idx}
                              className="pp-premium-dropdown-item"
                              active={pId === currentPatientId}
                              onClick={() => handlePatientSwitch(pId)}
                            >
                              <span style={{ flex: 1 }}>{name}</span>
                              {pId === currentPatientId && (
                                <svg width="13" height="10" viewBox="0 0 13 10" fill="none" style={{ marginLeft: "8px", flexShrink: 0 }}>
                                  <path d="M1 5l4 4L12 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </Dropdown.Item>
                          );
                        })}
                      </Dropdown.Menu>
                    </Dropdown>
                    {showPatientHint && (
                      <div className="pp-dropdown-hint pp-dropdown-hint--push-left" onClick={() => setShowPatientHint(false)}>
                        Change patient
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="pp-user-pill" style={{ cursor: "default" }}>
                    <div className="pp-user-pill__avatar">{patientNameInitials}</div>
                    <div className="pp-user-pill__name">
                      <div className="pp-user-pill__welcome">Welcome</div>
                      <div className="pp-user-pill__fullname">{patientName}</div>
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{ position: "relative", display: "inline-block", marginLeft: "16px" }}
                className="pp-loc-tag-wrap"
              >
                {availableLocations.length > 1 ? (
                  <>
                    <Dropdown
                      className="d-inline-block"
                      onToggle={(isOpen) => {
                        if (isOpen) {
                          setShowLocationHint(false);
                          setShowPatientHint(false);
                        }
                      }}
                    >
                      <Dropdown.Toggle as={LocationToggle} id="dropdown-location">
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "250px", display: "inline-block", verticalAlign: "bottom" }}>
                          {locationLabel || "No location assigned"}
                        </span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: "4px" }}><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="pp-premium-dropdown">
                        {availableLocations.map((loc, idx) => {
                          const eId = `${loc.doctorId}-${loc.doctorSpecialtyId}-${loc.providerId}`;
                          const docName = loc.doctorName || "";
                          const spec = loc.doctorSpecialtyName || "";
                          const prov = loc.providerName || "";
                          const pName = prov.includes("-") ? prov.split("-")[0].trim() : prov;
                          const label = [docName, spec, pName].filter(Boolean).join("-") || `Location ${idx + 1}`;

                          return (
                            <Dropdown.Item
                              key={eId || idx}
                              className="pp-premium-dropdown-item"
                              active={eId ===`${tokenData?.doctorId}-${tokenData?.doctorSpecialtyId}-${tokenData?.providerId}`}
                              onClick={() => handleLocationSwitch(eId)}
                            >
                              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                              {eId === (tokenData?.eventId || tokenData?.EventId) && (
                                <svg width="13" height="10" viewBox="0 0 13 10" fill="none" style={{ marginLeft: "8px", flexShrink: 0 }}>
                                  <path d="M1 5l4 4L12 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </Dropdown.Item>
                          );
                        })}
                      </Dropdown.Menu>
                    </Dropdown>
                    {showLocationHint && (
                      <div className="pp-dropdown-hint pp-dropdown-hint--push-right" onClick={() => setShowLocationHint(false)}>
                        Change location
                      </div>
                    )}
                  </>
                ) : (
                  <span
                    className="pp-tag pp-tag--teal"
                    style={{
                      cursor: "default",
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "250px", display: "inline-block", verticalAlign: "bottom" }}>
                      {locationLabel || "No location assigned"}
                    </span>
                  </span>
                )}
                {availableLocations.length <= 1 && (
                  <div className="pp-loc-tooltip">
                    {[doctorName, specialtyName, providerRaw]
                      .filter(Boolean)
                      .join("-")}
                  </div>
                )}
              </div>
            </div>

            <div className="pp-topbar__right">
              {ACTIVE_STEPS.length > 1 && (
                <div className="pp-progress-pill">
                  <div
                    className="pp-progress-ring"
                    style={{
                      background: `conic-gradient(${C.t400} ${pct * 3.6}deg, ${C.s100} 0)`,
                    }}
                  >
                    <div className="pp-progress-ring__inner">
                      <span className="pp-progress-ring__pct">{pct}%</span>
                    </div>
                  </div>
                  <div>
                    <div className="pp-progress-label">Progress</div>
                    <div className="pp-progress-step">
                      Step {step + 1} / {ACTIVE_STEPS.length}
                    </div>
                  </div>
                </div>
              )}


              <button
                className="pp-logout-btn pp-btn-ghost pp-btn-logout"
                onClick={handleLogout}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* ── Step tab row — auto-saves before switching ── */}
          {ACTIVE_STEPS.length > 1 && (
            <ScrollableStepTabs
              steps={ACTIVE_STEPS}
              step={step}
              setStep={handleStepChange}
              stepPendingMap={stepPendingMap}
              getStepSubLabel={getStepSubLabel}
              getStepSubColor={getStepSubColor}
            />
          )}
        </header>

        <main className={`pp-main${isFullHeightStep ? " pp-main--intake" : ""}`}>
          {/* ── Page heading ── */}
          <div className="pp-page-heading">
            <div className="pp-page-heading__desc-row">
              <div className="pp-page-heading__desc">
                {STEP_DESCS[currentStepId]}
                {ACTIVE_STEPS.length > 1 && (
                  <span
                    className="pp-tag pp-tag--teal"
                    style={{ marginLeft: 10, verticalAlign: "middle" }}
                  >
                    Step {step + 1} / {ACTIVE_STEPS.length}
                  </span>
                )}
              </div>
              {currentStepId === "intake" && (
                <label className="pp-copy-sign-check">
                  <input
                    type="checkbox"
                    checked={copySignature}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const hasSig =
                          intakeSheetRef.current?.hasSignature?.() ?? false;
                        if (!hasSig) {
                          toast.error(
                            "Signature is required.",
                          );
                          return;
                        }
                      }
                      setCopySignature(e.target.checked);
                    }}
                  />
                  <span className="pp-copy-sign-check__box">
                    {copySignature && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <polyline
                          points="2,6 5,9 10,3"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="pp-copy-sign-check__label">
                    Apply signature
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className={`pp-card${isFullHeightStep ? " pp-card--intake" : ""}`}>
            <div className="pp-card__top-bar" />

            <div
              className={`pp-anim-step${isFullHeightStep ? " pp-anim-step--intake" : ""}`}
            >
              {/* Step 0 — Patient Details */}
              <div style={{ display: currentStepId === "patient" ? "block" : "none" }}>
                {mountedSteps[0] && (
                  <PatientDetail
                    key={`patient-${refreshKey}`}
                    ref={patientDetailRef}
                    onApiSuccess={handleStepSuccess}
                    onReady={handlePatientReady}
                  />
                )}
              </div>

              {/* Step 1 — Intake Sheets (needs internal scroll) */}
              <div
                style={{
                  display: currentStepId === "intake" ? "flex" : "none",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                }}
              >
                {mountedSteps[1] && (
                  <IntakeSheet
                    key={`intake-${refreshKey}`}
                    ref={intakeSheetRef}
                    onApiSuccess={handleStepSuccess}
                    copySignature={copySignature}
                    onPendingChange={setIntakeHasPending}
                    onTabInfoChange={setIntakeTabInfo}
                  />
                )}
              </div>

              {/* Step 2 — Documents */}
              <div
                style={{
                  display: currentStepId === "documents" ? "flex" : "none",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                }}
              >
                {mountedSteps[2] && (
                  <DocumentUpload
                    key={`docs-${refreshKey}`}
                    ref={docUploadRef}
                    nodes={tokenDocuments}
                    onApiSuccess={handleStepSuccess}
                    uploadedDocs={uploadedDocs}
                    onUploadedDocsChange={setUploadedDocs}
                    onPendingChange={setDocsHasPending}
                  />
                )}
              </div>

              {/* Step 3 — Medical Notes / Questionnaire */}
              {/* <div style={{ display: step === 3 ? "block" : "none" }}>
                {mountedSteps[3] && (
                  <MedicalNotes
                    ref={medicalNotesRef}
                    onApiSuccess={handleStepSuccess}
                  />
                )}
              </div> */}
            </div>
            {/* end pp-anim-step */}

            {/* ── Footer ── always visible, never scrolls away ── */}
            <div className="pp-card-footer" style={{ flexShrink: 0 }}>
              <p className="pp-card-footer__security">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.t400}
                  strokeWidth="2.5"
                  style={{ verticalAlign: "-2px", marginRight: 4 }}
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Your Information is Encrypted &amp; Secure
              </p>
              <div className="pp-card-footer__btns">
                {step > 0 && (
                  <button
                    className="pp-btn-back"
                    onClick={() => setStep((s) => s - 1)}
                  >
                    ← Back
                  </button>
                )}
                <button className="pp-btn-save" onClick={handleSaveAndContinue}>
                  {(() => {
                    const isLastPortalStep = step === ACTIVE_STEPS.length - 1;
                    const isIntakeLastOrOnlyTab = intakeTabInfo.isLastTab || intakeTabInfo.isOnlyTab;
                    if (currentStepId === "intake") {
                      if (isLastPortalStep) {
                        return isIntakeLastOrOnlyTab ? "Finish" : "Save & Continue →";
                      }
                      return "Save & Continue →";
                    }
                    if (isLastPortalStep) {
                      return ACTIVE_STEPS.length === 1 ? "Update" : "Finish";
                    }
                    return "Save & Continue →";
                  })()}
                </button>
              </div>
            </div>
          </div>
          {/* end pp-card */}
        </main>
      </div>
    </>
  );
}